"use client";

import React, { useEffect, useState } from "react";
import type {
  HeroSectionData,
  HeroLayeredSlide,
  SectionProps,
} from "@/themes/default/templates/section/types";
import { SlideLayers } from "@/themes/default/templates/section/_shared/SlideLayers";

/**
 * epona Hero — LAYERED variant.
 *
 * Reproduces the legacy Epona V2 layered banner (Revolution Slider
 * `tp-caption`): a cross-fading slider whose slides each carry a set of
 * FREE-POSITIONED content layers (heading / text / image / box). Each
 * layer is anchored (9-point) with a % offset so it stays responsive, and
 * animates in (fade / fade-up / … / zoom) with a per-layer delay when its
 * slide becomes active.
 *
 * Size comes from the section `height` setting (small/medium/large/
 * fullscreen) — no separate height control here.
 */

export function HeroLayered({
  section,
  currentLanguage = "en",
  sectionNodes,
}: SectionProps<HeroSectionData> & {
  sectionNodes?: Record<string, React.ReactNode>;
}) {
  const slides: HeroLayeredSlide[] = Array.isArray(section.slides) && section.slides.length
    ? section.slides
    : section.backgroundImage
      ? [{ id: "s0", backgroundImage: section.backgroundImage, layers: [] }]
      : [];

  const [active, setActive] = useState(0);
  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => setActive((i) => (i + 1) % slides.length), 6500);
    return () => clearInterval(id);
  }, [slides.length]);

  const heightKey = ((section as any).height as string) || "large";
  const minHeight =
    heightKey === "fullscreen"
      ? "calc(100dvh - var(--site-header-h, var(--header-h-default, 130px)))"
      : heightKey === "small"
        ? "42vh"
        : heightKey === "medium"
          ? "56vh"
          : "72vh";

  if (slides.length === 0) {
    return (
      <section
        className="relative w-full"
        style={{ minHeight, backgroundImage: "var(--gradient-primary)" }}
        data-section-id={section._id}
        data-section-type={section.type}
        data-hero-variant="layered"
      />
    );
  }

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ minHeight }}
      data-section-id={section._id}
      data-section-type={section.type}
      data-hero-variant="layered"
    >
      {slides.map((slide, i) => {
        const isActive = i === active;
        const ov = slide.overlay;
        const ovEnabled = ov?.enabled !== false;
        const ovOpacity = typeof ov?.opacity === "number" ? ov.opacity : 0.45;
        return (
          <div
            key={slide.id || i}
            className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
            style={{ opacity: isActive ? 1 : 0 }}
            aria-hidden={isActive ? undefined : true}
          >
            {/* Slide background. */}
            {slide.backgroundImage && (
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${slide.backgroundImage})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            )}
            {/* Scrim for legibility. */}
            {ovEnabled && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ backgroundColor: ov?.color || "#0B1B2B", opacity: ovOpacity }}
              />
            )}
            {/* Positioned layers (constrained to the content max-width). */}
            <div className="relative mx-auto h-full w-full max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="relative h-full w-full" style={{ minHeight }}>
                <SlideLayers
                  layers={slide.layers}
                  active={isActive}
                  currentLanguage={currentLanguage}
                  slideKey={active}
                  sectionNodes={sectionNodes}
                />
              </div>
            </div>
          </div>
        );
      })}

      {/* Slider dots. */}
      {slides.length > 1 && (
        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 gap-2.5">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === active}
              className="h-2.5 rounded-full transition-all"
              style={{
                backgroundColor:
                  i === active ? "var(--color-accent)" : "rgba(255,255,255,0.55)",
                width: i === active ? "1.6rem" : "0.625rem",
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default HeroLayered;
